/**
 * InCallUI - Active call view with controls, participants panel, and modals
 * Shared between all platforms with minor platform-specific adjustments
 */

'use client';

import React, { useState, useRef, useEffect } from 'react';
import { 
  Phone, 
  PhoneIncoming,
  PhoneOff, 
  PhoneForwarded,
  Video, 
  VideoOff,
  Mic,
  MicOff,
  Volume2,
  User,
  Users,
  UserPlus,
  UserMinus,
  Hand,
  Pause,
  Play,
  MonitorUp,
  PictureInPicture2,
  SwitchCamera,
  Sparkles,
  Settings,
  X,
  Search,
  Loader2,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  ArrowRightLeft,
  Copy,
  Link2,
  ParkingCircle,
  Circle,
  Smile,
  PenLine,
  DoorOpen,
  Grid3X3,
  BellOff,
  Bell,
  LogIn,
  LogOut,
  MessageSquare,
  Monitor,
  Smartphone,
  Globe,
  Zap,
  GitMerge,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { isCapacitor } from '@/lib/platformUtils';
import { Track } from 'livekit-client';
import type { InCallUIProps, CallParticipant, ActivityLogEntry, ActiveSession, SipExtension, OperationsUser } from '../types';
import { CallAvatar } from '../shared/CallAvatar';
import { CallTimer } from '../shared/CallTimer';
import { useTheme } from '@/contexts/ThemeContext';
import { BrandedSpinnerApp } from '../../../../remotion/components/BrandedSpinnerApp';

// Detect test/echo calls by caller name patterns
const isTestCall = (name: string) => {
  const n = name.toLowerCase();
  return n.includes('test call') || n.includes('echo test') || n.includes('audio test') || 
    n.includes('welcome call') || n === 'echo' || n === 'test';
};

// Reaction constants
const CALL_REACTIONS = [
  { emoji: '👍', label: 'Thumbs Up' },
  { emoji: '👏', label: 'Clap' },
  { emoji: '❤️', label: 'Love' },
  { emoji: '😂', label: 'Laugh' },
  { emoji: '😮', label: 'Wow' },
  { emoji: '🎉', label: 'Celebrate' },
];

// DTMF tone frequencies (standard dual-tone multi-frequency)
const DTMF_FREQUENCIES: Record<string, [number, number]> = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};

// DTMF digit to RFC 4733 event code mapping
const DTMF_CODES: Record<string, number> = {
  '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5,
  '6': 6, '7': 7, '8': 8, '9': 9, '*': 10, '#': 11,
};

// Play a DTMF tone locally for user feedback
function playDtmfTone(digit: string, duration = 150) {
  try {
    const freqs = DTMF_FREQUENCIES[digit];
    if (!freqs) return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);
    gainNode.connect(ctx.destination);
    
    freqs.forEach(freq => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      osc.connect(gainNode);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration / 1000);
    });
    
    setTimeout(() => ctx.close(), duration + 100);
  } catch {
    // Audio context not available — silently ignore
  }
}

// Timer component for transfer-in-progress overlay
function TransferTimer({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = React.useState(0);
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);
  
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  
  return (
    <span className="text-gray-400 text-sm font-mono tabular-nums">
      {mins}:{secs.toString().padStart(2, '0')}
    </span>
  );
}

export function InCallUI({
  callType,
  callState,
  callDuration,
  outgoingCallProgress = 'idle',
  selectedUser,
  screenPopData,
  sipCallData,
  isGroupCall,
  groupCallInfo,
  participants,
  localTracksRef,
  roomRef,
  remoteVideoRefs,
  localVideoRef,
  // Control states
  isMuted,
  isVideoEnabled,
  isSpeakerOn,
  canToggleSpeaker,
  isOnHold,
  isBlurEnabled,
  isScreenSharing,
  isHandRaised,
  isRecording,
  recordingStartTime,
  // Remote states
  remoteScreenShareActive,
  remoteVideoActive,
  isFrontCamera,
  audioBlocked,
  // Actions
  onToggleMute,
  onToggleVideo,
  onToggleSpeaker,
  onToggleHold,
  onToggleBlur,
  onToggleScreenShare,
  onToggleHandRaise,
  onToggleRecording,
  onSwitchCamera,
  onSendReaction,
  onEnableAudio,
  onEndCall,
  onPopOut,
  onTransfer,
  onParkCall,
  onAddParticipant,
  addingParticipant,
  onGenerateInviteLink,
  generatingLink,
  showInviteLink,
  inviteLink,
  onCopyInviteLink,
  onCloseInviteLink,
  onHapticFeedback,
  // UI state
  isDark,
  primaryColor,
  // Modals/panels state
  showParticipantsPanel,
  onToggleParticipantsPanel,
  showActivityLog,
  onToggleActivityLog,
  activityLog,
  // Active reactions
  activeReactions,
  // Participant hand raises
  participantHandRaises,
  handsRaisedCount,
  // Waiting room
  waitingParticipants,
  onAdmitParticipant,
  onDenyParticipant,
  // Breakout rooms
  isBreakoutActive,
  breakoutRooms,
  onShowBreakoutModal,
  // Whiteboard
  meetingSettingsConfig,
  showWhiteboard,
  onToggleWhiteboard,
  remotePresenterBoardActive = false,
  remotePresenterBoardName,
  // SIP-specific props
  isSipCall,
  isGateCall,
  onSendDtmf,
  // Device switcher
  activeSessions,
  loadingSessions,
  onTransferToDevice,
  transferringTo,
  // Extension list for transfer
  extensionList,
  loadingExtensions,
  // Pending transfer state
  pendingTransfer,
  onCancelTransfer,
  // Attended transfer consultation
  attendedTransferState,
  onCompleteTransfer,
  onCancelAttendedTransfer,
  onMergeConference,
  attendedTransferSubmitting = null,
  secondLineIncoming = null,
  onAnswerSecondLine,
  onDeclineSecondLine,
  activeCallLines,
  onSelectActiveCallLine,
  // Operations users for contacts-style transfer
  operationsUsers,
  isUserOnline,
  // Parking slots
  parkingSlots,
  loadingParkingSlots,
  // Participant menu
  selectedParticipantMenu,
  onSelectParticipantMenu,
  onRequestParticipantMute,
  onRemoveParticipant,
  // Call start time (for debounce)
  callStartTime,
  layoutVariant = 'slideover',
}: InCallUIProps) {
  const { clientBranding } = useTheme();

  const outboundStatusText = React.useMemo(() => {
    if (outgoingCallProgress === 'idle') return null;
    if (callState === 'connected') return null;

    switch (outgoingCallProgress) {
      case 'connecting':
        return 'Connecting...';
      case 'calling':
        return 'Calling...';
      case 'ringing':
        return 'Ringing...';
      default:
        return null;
    }
  }, [callState, outgoingCallProgress]);

  // Local UI state
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showDeviceSwitcher, setShowDeviceSwitcher] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showParkingDialog, setShowParkingDialog] = useState(false);
  const [showAddParticipants, setShowAddParticipants] = useState(false);
  const [showCallSettings, setShowCallSettings] = useState(false);
  const [showDtmfKeypad, setShowDtmfKeypad] = useState(false);
  const [dtmfBuffer, setDtmfBuffer] = useState('');
  const [transferSearch, setTransferSearch] = useState('');
  const [addParticipantSearch, setAddParticipantSearch] = useState('');
  
  // Determine if this is a video call
  const isVideoCall = callType === 'video' || isVideoEnabled;
  
  // Detect test calls
  const callerDisplayName = screenPopData?.contact?.company_name 
    || screenPopData?.contact?.contact_person
    || (selectedUser ? `${selectedUser?.first_name || ''} ${selectedUser?.last_name || ''}`.trim() : 'Unknown');
  const testCall = isTestCall(callerDisplayName);
  
  // Format duration helper
  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Build transfer targets: merge extensions with operations users for avatar/DND info
  const transferTargets = React.useMemo(() => {
    // Create a map of user_id -> OperationsUser for quick lookup
    const userMap = new Map<number, OperationsUser>();
    (operationsUsers || []).forEach(u => userMap.set(u.id, u));
    
    return extensionList
      .filter(ext => ext.user_id) // Only extensions with assigned users
      .map(ext => {
        const opUser = ext.user_id ? userMap.get(ext.user_id) : undefined;
        return {
          ...ext,
          avatar_url: opUser?.avatar_url,
          first_name: opUser?.first_name || ext.display_name,
          last_name: opUser?.last_name || '',
          is_online: opUser ? (isUserOnline ? isUserOnline(opUser) : false) : false,
          is_dnd: ext.do_not_disturb || false,
        };
      })
      .sort((a, b) => {
        // DND users go to the bottom
        if (a.is_dnd && !b.is_dnd) return 1;
        if (!a.is_dnd && b.is_dnd) return -1;
        // Online users first
        if (a.is_online && !b.is_online) return -1;
        if (!a.is_online && b.is_online) return 1;
        return a.display_name.localeCompare(b.display_name);
      });
  }, [extensionList, operationsUsers, isUserOnline]);
  
  // Filter transfer targets by search
  const filteredTransferTargets = transferTargets.filter(t => {
    if (!transferSearch) return true;
    const search = transferSearch.toLowerCase();
    const fullName = `${t.first_name} ${t.last_name}`.toLowerCase();
    return fullName.includes(search) ||
           t.display_name.toLowerCase().includes(search) ||
           t.extension_number.includes(transferSearch) ||
           (t.friendly_extension && t.friendly_extension.includes(transferSearch));
  });

  /** Secondary line under display name (number, SIP meta, or email). */
  const railSubtitle = React.useMemo(() => {
    if (isSipCall && sipCallData) {
      return (
        sipCallData.caller_number ||
        sipCallData.caller_name ||
        sipCallData.did_number ||
        sipCallData.extension_display_name ||
        sipCallData.queue_name ||
        ''
      );
    }
    if (selectedUser?.email?.trim()) return selectedUser.email.trim();
    return '';
  }, [isSipCall, sipCallData, selectedUser]);
  
  const handleEndCall = () => {
    // Debounce - don't allow ending call within 2 seconds of starting
    const timeSinceStart = Date.now() - callStartTime;
    if (timeSinceStart < 2000) return;
    
    onHapticFeedback('heavy');
    onEndCall();
  };
  
  const handleSendReaction = (emoji: string) => {
    onSendReaction(emoji);
    setShowReactionPicker(false);
  };
  
  // Handle DTMF digit press
  const handleDtmfDigit = (digit: string) => {
    playDtmfTone(digit);
    onHapticFeedback('light');
    setDtmfBuffer(prev => (prev + digit).slice(-20)); // Keep last 20 digits
    onSendDtmf?.(digit);
  };
  
  // DTMF keypad layout
  const dtmfDigits = [
    { num: '1', letters: '' },
    { num: '2', letters: 'ABC' },
    { num: '3', letters: 'DEF' },
    { num: '4', letters: 'GHI' },
    { num: '5', letters: 'JKL' },
    { num: '6', letters: 'MNO' },
    { num: '7', letters: 'PQRS' },
    { num: '8', letters: 'TUV' },
    { num: '9', letters: 'WXYZ' },
    { num: '*', letters: '' },
    { num: '0', letters: '+' },
    { num: '#', letters: '' },
  ];
  
  return (
    <div
      className={cn(
        'flex flex-col bg-gray-900 overflow-hidden',
        layoutVariant === 'embedded'
          ? 'relative h-full min-h-0 w-full rounded-xl'
          : 'fixed inset-0 z-[9999]',
      )}
    >
      {/* Safe area top spacer - use max() for Android Capacitor CSS variable fallback */}
      <div className="h-[max(env(safe-area-inset-top),var(--safe-area-top,0px))] flex-shrink-0 bg-black" />
      
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        {/* Wide-screen split: identity + telephony lane (softphone-style) */}
        <aside
          className={cn(
            'hidden min-h-0 min-w-[280px] max-w-sm flex-[0_0_min(22rem,32vw)] flex-col border-gray-800 bg-gradient-to-b from-gray-950 via-gray-950 to-black lg:flex',
            'border-b lg:border-b-0 lg:border-r',
          )}
          aria-label="Call session details"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              Total Connect
            </p>

            {activeCallLines &&
              activeCallLines.length > 0 &&
              (callState === 'connected' ||
                callState === 'connecting' ||
                callState === 'reconnecting') && (
              <div className="rounded-xl border border-gray-800 bg-black/40 p-3">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Active calls
                </p>
                <ul className="flex flex-col gap-2">
                  {activeCallLines.map((line) => {
                    const selectable = line.selectable !== false;
                    const rowStyles = cn(
                      'flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left transition-colors',
                      line.isFocused && 'ring-2 ring-white/25',
                      line.state === 'consultation'
                        ? 'bg-green-500/15 ring-1 ring-green-500/30'
                        : line.state === 'ringing'
                          ? 'bg-blue-500/10 ring-1 ring-blue-500/25'
                          : line.state === 'held'
                            ? 'bg-amber-500/10 ring-1 ring-amber-500/20'
                            : selectable
                              ? 'bg-white/5 hover:bg-white/10'
                              : 'cursor-default bg-white/5',
                    );
                    const rowBody = (
                      <>
                        <span
                          className={cn(
                            'mt-1 h-2 w-2 shrink-0 rounded-full',
                            line.state === 'active' && 'bg-emerald-400',
                            line.state === 'held' && 'bg-amber-400',
                            line.state === 'consultation' &&
                              'animate-pulse bg-green-400',
                            line.state === 'ringing' &&
                              'animate-pulse bg-blue-400',
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-white">
                            {line.label}
                          </span>
                          {line.subtitle ? (
                            <span className="block truncate text-[11px] text-gray-500">
                              {line.subtitle}
                            </span>
                          ) : null}
                          <span className="mt-0.5 block text-[10px] uppercase tracking-wide text-gray-600">
                            {line.state === 'held'
                              ? 'On hold'
                              : line.state === 'consultation'
                                ? 'Consultation'
                                : line.state === 'ringing'
                                  ? 'Ringing'
                                  : 'Live'}
                          </span>
                        </span>
                      </>
                    );
                    return (
                      <li key={line.id}>
                        {selectable ? (
                          <button
                            type="button"
                            onClick={() => onSelectActiveCallLine?.(line.id)}
                            className={rowStyles}
                          >
                            {rowBody}
                          </button>
                        ) : (
                          <div role="status" className={rowStyles}>
                            {rowBody}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="flex justify-center">
              <div
                className={cn(
                  'relative flex h-[104px] w-[104px] items-center justify-center overflow-hidden rounded-full ring-2',
                  isSipCall ? 'ring-purple-400/35' : 'ring-emerald-400/35',
                )}
              >
                {testCall ? (
                  <BrandedSpinnerApp
                    logoUrl={clientBranding?.logo_url}
                    primaryColor={primaryColor}
                    size={96}
                    opacity={0.95}
                  />
                ) : isGroupCall ? (
                  <Users className="h-12 w-12 text-slate-400" />
                ) : screenPopData?.contact?.logo_url ||
                  screenPopData?.contact?.photo_url ||
                  selectedUser?.avatar_url ? (
                  <img
                    src={
                      screenPopData?.contact?.logo_url ||
                      screenPopData?.contact?.photo_url ||
                      selectedUser?.avatar_url
                    }
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : isSipCall ? (
                  <Globe className="h-12 w-12 text-slate-400" />
                ) : (
                  <User className="h-12 w-12 text-slate-400" />
                )}
              </div>
            </div>

            <div className="text-center">
              <h2 className="line-clamp-2 text-lg font-bold leading-tight text-white">
                {callerDisplayName}
              </h2>
              {railSubtitle ? (
                <p className="mt-1 line-clamp-2 font-mono text-xs text-gray-400">{railSubtitle}</p>
              ) : null}
              <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-gray-300">
                  {callType === 'video' ? 'Video' : 'Voice'}
                </span>
                {isSipCall && (
                  <span className="rounded-full bg-purple-500/25 px-2 py-0.5 text-[10px] font-medium text-purple-200">
                    SIP
                  </span>
                )}
                {isGateCall && (
                  <span className="rounded-full bg-amber-500/25 px-2 py-0.5 text-[10px] font-medium text-amber-200">
                    Gate call
                  </span>
                )}
                {isGroupCall && (
                  <span className="rounded-full bg-blue-500/25 px-2 py-0.5 text-[10px] font-medium text-blue-200">
                    Conference
                  </span>
                )}
              </div>
              <div className="mt-3 flex justify-center">
                <CallTimer
                  callState={callState}
                  callDuration={callDuration}
                  isOnHold={isOnHold}
                  statusTextOverride={outboundStatusText}
                />
              </div>
            </div>

            {(callState === 'connected' || callState === 'initiating' || callState === 'connecting') && (
              <div
                className={cn(
                  'grid gap-2 border-t border-gray-800/80 pt-4',
                  isSipCall && callState === 'connected' ? 'grid-cols-5' : 'grid-cols-4',
                )}
              >
                <button
                  type="button"
                  onClick={() => {
                    onHapticFeedback('light');
                    onToggleMute();
                  }}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-xl py-2 transition-colors',
                    isMuted ? 'bg-white text-gray-900' : 'bg-white/10 text-white hover:bg-white/15',
                  )}
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                  <span className="text-[9px] font-medium opacity-90">{isMuted ? 'Unmute' : 'Mute'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    onHapticFeedback('light');
                    onToggleParticipantsPanel(!showParticipantsPanel);
                  }}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-xl py-2 transition-colors',
                    showParticipantsPanel ? 'bg-blue-600 text-white' : 'bg-white/10 text-white hover:bg-white/15',
                  )}
                  title="Participants"
                >
                  <Users className="h-5 w-5" />
                  <span className="text-[9px] font-medium opacity-90">People</span>
                </button>

                {isSipCall && callState === 'connected' && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        onHapticFeedback('light');
                        setShowDtmfKeypad(true);
                        setDtmfBuffer('');
                      }}
                      className={cn(
                        'flex flex-col items-center gap-1 rounded-xl py-2 transition-colors',
                        showDtmfKeypad ? 'bg-blue-600 text-white' : 'bg-white/10 text-white hover:bg-white/15',
                      )}
                      title="Keypad"
                    >
                      <Grid3X3 className="h-5 w-5" />
                      <span className="text-[9px] font-medium opacity-90">Keys</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onHapticFeedback('light');
                        setShowTransferDialog(true);
                      }}
                      className="flex flex-col items-center gap-1 rounded-xl bg-white/10 py-2 text-white transition-colors hover:bg-white/15"
                      title="Transfer"
                    >
                      <PhoneForwarded className="h-5 w-5" />
                      <span className="text-[9px] font-medium opacity-90">Xfer</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onHapticFeedback('light');
                        onToggleVideo();
                      }}
                      className={cn(
                        'flex flex-col items-center gap-1 rounded-xl py-2 transition-colors',
                        isVideoEnabled ? 'bg-blue-600 text-white' : 'bg-white/10 text-white hover:bg-white/15',
                      )}
                      title="Camera"
                    >
                      {isVideoEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                      <span className="text-[9px] font-medium opacity-90">Video</span>
                    </button>
                  </>
                )}

                {!isSipCall && callState === 'connected' && (
                  <button
                    type="button"
                    onClick={() => {
                      onHapticFeedback('light');
                      onToggleVideo();
                    }}
                    className={cn(
                      'flex flex-col items-center gap-1 rounded-xl py-2 transition-colors',
                      isVideoEnabled ? 'bg-blue-600 text-white' : 'bg-white/10 text-white hover:bg-white/15',
                    )}
                    title="Camera"
                  >
                    {isVideoEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                    <span className="text-[9px] font-medium opacity-90">Video</span>
                  </button>
                )}
              </div>
            )}

            {/* Visitor-management gate call — resident authorizes/denies entry.
                Emits DTMF 5/9 so the guard's panel (and any PSTN path) resolves
                identically to a phone keypad press. */}
            {isGateCall && callState === 'connected' && onSendDtmf && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                <p className="mb-2 text-center text-[11px] font-medium text-amber-200">
                  A visitor is at the gate asking for access
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      onHapticFeedback('medium');
                      handleDtmfDigit('5');
                    }}
                    className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                  >
                    <DoorOpen className="h-4 w-4" />
                    Authorize
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onHapticFeedback('medium');
                      handleDtmfDigit('9');
                    }}
                    className="flex items-center justify-center gap-1.5 rounded-lg bg-rose-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-rose-700"
                  >
                    <X className="h-4 w-4" />
                    Deny
                  </button>
                </div>
              </div>
            )}

            {callState === 'connected' && participants.length > 0 && (
              <p className="text-center text-[11px] text-gray-500">
                {participants.length + 1} people on this call
              </p>
            )}
          </div>

          {(callState === 'connected' || callState === 'initiating' || callState === 'connecting') && (
            <div className="flex-shrink-0 border-t border-gray-800 p-3 pb-[calc(0.75rem+max(env(safe-area-inset-bottom),var(--safe-area-bottom,0px)))]">
              <button
                type="button"
                onClick={handleEndCall}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 py-3 text-sm font-semibold text-white shadow-lg shadow-red-500/20 transition-colors hover:bg-red-500 active:scale-[0.98]"
              >
                <PhoneOff className="h-5 w-5" />
                End call
              </button>
            </div>
          )}
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
          {/* Participants Panel - Left side on desktop, overlay on mobile */}
        {showParticipantsPanel && (
          <div className={`absolute sm:relative inset-0 sm:inset-auto sm:w-72 bg-gray-800/95 sm:bg-gray-800 border-r border-gray-700 z-20 flex flex-col`}>
            {/* Panel Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-white" />
                <span className="text-white font-medium">
                  Participants ({participants.length + 1})
                </span>
              </div>
              <button
                onClick={() => onToggleParticipantsPanel(false)}
                className="p-1.5 hover:bg-gray-700 rounded-lg sm:hidden"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            {/* Local User */}
            <div className="px-3 py-2 border-b border-gray-700/50">
              <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-700/30">
                <div className="relative">
                  <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-gray-800 flex items-center justify-center">
                    {isMuted ? (
                      <MicOff className="w-2.5 h-2.5 text-red-400" />
                    ) : (
                      <Mic className="w-2.5 h-2.5 text-green-400" />
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">You</p>
                  <div className="flex items-center gap-2 text-xs">
                    {isVideoEnabled && <Video className="w-3 h-3 text-blue-400" />}
                    {isHandRaised && <Hand className="w-3 h-3 text-yellow-400 animate-bounce" />}
                    {isScreenSharing && <MonitorUp className="w-3 h-3 text-green-400" />}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Waiting Participants (Lobby) */}
            {waitingParticipants.length > 0 && (
              <div className="px-3 py-2 border-b border-gray-700/50">
                <p className="text-xs text-amber-400 font-medium mb-2 flex items-center gap-1">
                  <DoorOpen className="w-3.5 h-3.5" />
                  Waiting Room ({waitingParticipants.length})
                </p>
                <div className="space-y-1">
                  {waitingParticipants.map((participant) => (
                    <div key={participant.identity} className="flex items-center gap-2 p-2 bg-amber-500/10 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-amber-600/30 flex items-center justify-center">
                        <User className="w-4 h-4 text-amber-400" />
                      </div>
                      <span className="flex-1 text-amber-200 text-sm truncate">{participant.name}</span>
                      <button
                        onClick={() => onAdmitParticipant?.(participant.userId || 0)}
                        className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                      >
                        Admit
                      </button>
                      <button
                        onClick={() => onDenyParticipant?.(participant.userId || 0)}
                        className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                      >
                        Deny
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Remote Participants */}
            <div className="flex-1 overflow-y-auto px-3 py-2">
              {participants.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">Waiting for others to join...</p>
              ) : (
                participants.map((participant) => (
                  <div key={participant.identity} className="flex items-center gap-3 p-2 hover:bg-gray-700/30 rounded-lg">
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden">
                        {participant.avatar_url ? (
                          <img src={participant.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-gray-800 flex items-center justify-center">
                        {participant.isMuted ? (
                          <MicOff className="w-2.5 h-2.5 text-red-400" />
                        ) : participant.isSpeaking ? (
                          <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
                        ) : (
                          <Mic className="w-2.5 h-2.5 text-green-400" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{participant.name}</p>
                      <div className="flex items-center gap-2">
                        {participant.hasVideo && <Video className="w-3 h-3 text-blue-400" />}
                        {participant.isSpeaking && <span className="text-green-400 text-xs">Speaking</span>}
                      </div>
                    </div>
                    {participant.isHandRaised && (
                      <Hand className="w-5 h-5 text-yellow-400 animate-bounce" />
                    )}
                    
                    {/* Participant Actions Menu */}
                    <div className="relative">
                      <button
                        onClick={() => onSelectParticipantMenu(
                          selectedParticipantMenu === participant.identity ? null : participant.identity
                        )}
                        className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                      </button>
                      
                      {selectedParticipantMenu === participant.identity && (
                        <div className="absolute right-0 top-full mt-1 w-44 bg-gray-800 rounded-lg shadow-xl border border-gray-700 py-1 z-30">
                          {!participant.isMuted && (
                            <button
                              onClick={() => onRequestParticipantMute(participant.identity, participant.name)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                            >
                              <MicOff className="w-4 h-4" />
                              <span>Ask to Mute</span>
                            </button>
                          )}
                          <button
                            onClick={() => onRemoveParticipant(participant.identity, participant.name)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-gray-700 transition-colors"
                          >
                            <UserMinus className="w-4 h-4" />
                            <span>Remove from Call</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Activity Log Toggle */}
            <div className="border-t border-gray-700">
              <button
                onClick={() => onToggleActivityLog(!showActivityLog)}
                className="w-full flex items-center justify-between px-4 py-3 text-gray-300 hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-medium">Activity & Interactions</span>
                  {activityLog.length > 0 && (
                    <span className="px-1.5 py-0.5 bg-blue-500/30 text-blue-300 rounded text-xs">{activityLog.length}</span>
                  )}
                </div>
                {showActivityLog ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </button>
              
              {showActivityLog && (
                <div className="max-h-48 overflow-y-auto px-2 pb-3 space-y-1.5">
                  {/* Hands Raised Summary */}
                  {handsRaisedCount > 0 && (
                    <div className="sticky top-0 bg-yellow-500/20 border border-yellow-500/30 rounded-lg px-3 py-2 mb-2">
                      <div className="flex items-center gap-2">
                        <Hand className="w-4 h-4 text-yellow-400 animate-bounce" />
                        <span className="text-yellow-300 text-sm font-medium">
                          {handsRaisedCount} hand{handsRaisedCount > 1 ? 's' : ''} raised
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {activityLog.length === 0 ? (
                    <p className="text-gray-500 text-xs text-center py-4">
                      No activity yet
                    </p>
                  ) : (
                    activityLog.slice(0, 30).map(entry => (
                      <div 
                        key={entry.id} 
                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${
                          entry.type === 'hand_raised' ? 'bg-yellow-500/10 border border-yellow-500/20' :
                          entry.type === 'reaction' ? 'bg-purple-500/10 border border-purple-500/20' :
                          entry.type === 'join' ? 'bg-green-500/10' :
                          entry.type === 'leave' ? 'bg-red-500/10' :
                          'bg-gray-800/30'
                        }`}
                      >
                        {entry.type === 'join' && <LogIn className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />}
                        {entry.type === 'leave' && <LogOut className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />}
                        {entry.type === 'hand_raised' && <Hand className="w-3.5 h-3.5 text-yellow-400 animate-bounce flex-shrink-0" />}
                        {entry.type === 'hand_lowered' && <Hand className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />}
                        {entry.type === 'mute' && <MicOff className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />}
                        {entry.type === 'unmute' && <Mic className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />}
                        {entry.type === 'video_on' && <Video className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />}
                        {entry.type === 'video_off' && <VideoOff className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                        {entry.type === 'screen_share_start' && <MonitorUp className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />}
                        {entry.type === 'screen_share_stop' && <MonitorUp className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                        {entry.type === 'reaction' && <span className="text-base flex-shrink-0">{entry.emoji}</span>}
                        <span className="flex-1 truncate">
                          <span className="text-white font-medium">{entry.participantName}</span>
                          {' '}
                          <span className="text-gray-400">
                            {entry.type === 'join' && 'joined the call'}
                            {entry.type === 'leave' && 'left the call'}
                            {entry.type === 'hand_raised' && 'raised their hand'}
                            {entry.type === 'hand_lowered' && 'lowered their hand'}
                            {entry.type === 'mute' && 'muted'}
                            {entry.type === 'unmute' && 'unmuted'}
                            {entry.type === 'video_on' && 'turned on camera'}
                            {entry.type === 'video_off' && 'turned off camera'}
                            {entry.type === 'screen_share_start' && 'started sharing screen'}
                            {entry.type === 'screen_share_stop' && 'stopped sharing'}
                            {entry.type === 'reaction' && `reacted`}
                          </span>
                        </span>
                        <span className="text-gray-500 text-[10px] flex-shrink-0">
                          {format(entry.timestamp, 'HH:mm')}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Recording Indicator */}
          {isRecording && callState === 'connected' && (
            <div className="absolute top-4 left-4 z-30 flex items-center gap-2 bg-red-600 rounded-full px-3 py-1.5 animate-pulse">
              <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
              <span className="text-white text-sm font-bold">REC</span>
              {recordingStartTime && (
                <span className="text-white/80 text-xs">
                  {Math.floor((Date.now() - recordingStartTime.getTime()) / 60000)}m
                </span>
              )}
            </div>
          )}
          
          {/* Floating Reactions Display */}
          {activeReactions.length > 0 && callState === 'connected' && (
            <div className="absolute top-4 right-4 z-30 flex flex-col gap-2 pointer-events-none">
              {activeReactions.map((reaction) => (
                <div
                  key={reaction.id}
                  className="flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1.5 animate-in slide-in-from-right fade-in duration-300"
                >
                  <span className="text-2xl animate-bounce">{reaction.emoji}</span>
                  <span className="text-white text-sm font-medium">{reaction.userName}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Video Area */}
          {isVideoEnabled || callType === 'video' || remoteScreenShareActive || remoteVideoActive ? (
            <div className="flex-1 relative">
              <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                {participants.length > 0 && (participants[0].hasVideo || remoteScreenShareActive || remoteVideoActive) ? (
                  <video
                    ref={(el) => {
                      if (el && participants[0]) {
                        const identity = participants[0].identity;
                        remoteVideoRefs.current.set(identity, el);
                        if (roomRef.current) {
                          const participant = roomRef.current.remoteParticipants.get(identity);
                          if (participant) {
                            participant.videoTrackPublications.forEach(pub => {
                              if (pub.track && pub.isSubscribed) {
                                pub.track.attach(el);
                              }
                            });
                            const screenSharePub = participant.getTrackPublication(Track.Source.ScreenShare);
                            if (screenSharePub?.track && screenSharePub.isSubscribed) {
                              screenSharePub.track.attach(el);
                            }
                          }
                        }
                      }
                    }}
                    autoPlay
                    playsInline
                    className="w-full h-full object-contain bg-black"
                  />
                ) : (
                  <div className="text-center">
                    {testCall ? (
                      <BrandedSpinnerApp
                        logoUrl={clientBranding?.logo_url}
                        primaryColor={primaryColor}
                        size={112}
                        opacity={0.95}
                      />
                    ) : (
                      <CallAvatar
                        user={selectedUser}
                        screenPopData={screenPopData}
                        isGroupCall={isGroupCall}
                        isSipCall={isSipCall}
                        isVideoCall={callType === 'video'}
                        size="lg"
                      />
                    )}
                    <p className="text-white text-xl font-medium mt-4">
                      {selectedUser?.first_name} {selectedUser?.last_name}
                    </p>
                    <CallTimer
                      callState={callState}
                      callDuration={callDuration}
                      isOnHold={isOnHold}
                      statusTextOverride={outboundStatusText}
                    />
                  </div>
                )}
              </div>
              
              {/* Local Video PIP */}
              {isVideoEnabled && (
                <div className="absolute top-4 right-4 w-32 h-44 rounded-xl overflow-hidden bg-gray-800 shadow-lg">
                  <video
                    ref={(el) => {
                      if (el) {
                        localVideoRef.current = el;
                        const videoTrack = localTracksRef.current.find(t => t.kind === Track.Kind.Video);
                        if (videoTrack) {
                          videoTrack.attach(el);
                        } else if (roomRef.current?.localParticipant) {
                          const pubTrack = roomRef.current.localParticipant.videoTrackPublications.values().next().value?.track;
                          if (pubTrack) pubTrack.attach(el);
                        }
                      }
                    }}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    style={{ transform: isFrontCamera ? 'scaleX(-1)' : 'none' }}
                  />
                </div>
              )}
            </div>
          ) : (
            /* Audio-only view - clean phone-style layout */
            <div className="flex-1 flex flex-col items-center justify-center">
              {/* Avatar / Branded Spinner for test calls */}
              <div className="relative mb-5">
                {testCall ? (
                  <BrandedSpinnerApp
                    logoUrl={clientBranding?.logo_url}
                    primaryColor={primaryColor}
                    size={112}
                    opacity={0.95}
                  />
                ) : (
                  <div className={`relative w-24 h-24 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden ring-2 ${
                    isSipCall ? 'ring-purple-400/40' : 'ring-emerald-400/40'
                  }`}>
                    {isGroupCall ? (
                      <Users className="w-10 h-10 text-slate-400" />
                    ) : screenPopData?.contact?.logo_url || screenPopData?.contact?.photo_url || selectedUser?.avatar_url ? (
                      <img 
                        src={screenPopData?.contact?.logo_url || screenPopData?.contact?.photo_url || selectedUser?.avatar_url} 
                        alt="" 
                        className="w-full h-full object-cover" 
                      />
                    ) : isSipCall ? (
                      <Globe className="w-10 h-10 text-slate-400" />
                    ) : (
                      <User className="w-10 h-10 text-slate-400" />
                    )}
                  </div>
                )}
              </div>
              
              {/* Caller Name */}
              <h1 className="text-white text-2xl font-bold mb-1.5">
                {callerDisplayName}
              </h1>
              
              {/* Timer */}
              <CallTimer
                callState={callState}
                callDuration={callDuration}
                isOnHold={isOnHold}
                statusTextOverride={outboundStatusText}
              />
            </div>
          )}

          {/* Audio Blocked Banner */}
          {audioBlocked && callState === 'connected' && (
            <button
              onClick={onEnableAudio}
              className="absolute top-20 left-1/2 -translate-x-1/2 px-6 py-3 bg-yellow-500 text-black rounded-full font-medium animate-pulse"
            >
              🔊 Tap to Enable Audio
            </button>
          )}

          {/* Second line (call waiting) — dual LiveKit */}
          {secondLineIncoming &&
            (callState === 'connected' ||
              callState === 'connecting' ||
              callState === 'reconnecting') &&
            !pendingTransfer?.active &&
            attendedTransferState?.status !== 'consulting' && (
            <div
              role="alert"
              data-testid="sip-second-line-incoming-banner"
              className="absolute top-14 left-3 right-3 z-[28] flex flex-wrap items-center gap-2 rounded-xl border border-blue-500/40 bg-gray-950/95 px-3 py-2.5 shadow-lg backdrop-blur-sm md:left-6 md:right-6"
            >
              <PhoneIncoming className="h-4 w-4 shrink-0 text-blue-400" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-white">Second line</p>
                <p className="truncate text-[11px] text-gray-400">
                  {secondLineIncoming.caller_number || secondLineIncoming.caller_name || 'Incoming call'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void onDeclineSecondLine?.()}
                className="shrink-0 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-medium text-gray-200 hover:bg-white/10"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={() => void onAnswerSecondLine?.()}
                className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-500"
              >
                Answer
              </button>
            </div>
          )}

          {/* On Hold Banner - only show when not in a pending transfer */}
          {isOnHold && callState === 'connected' && !pendingTransfer?.active && (
            <div className="absolute top-0 left-0 right-0 bg-yellow-500 px-4 py-2 flex items-center justify-center z-25">
              <Pause className="w-4 h-4 text-black mr-2" />
              <span className="text-black text-sm font-medium">Call on Hold</span>
            </div>
          )}

          {/* Transfer In Progress Overlay (Ringing phase) */}
          {pendingTransfer?.active && callState === 'connected' && (
            <div className="absolute inset-0 bg-gray-950 flex flex-col items-center justify-center z-30 px-6">
              {/* Animated phone icon */}
              <div className="relative mb-6">
                <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center animate-pulse">
                  <PhoneForwarded className="w-10 h-10 text-blue-400" />
                </div>
                <div className="absolute -inset-2 rounded-full border-2 border-blue-400/30 animate-ping" />
              </div>
              
              <h3 className="text-white text-lg font-semibold mb-1">Transferring Call</h3>
              <p className="text-gray-300 text-sm mb-1">
                Ringing <span className="text-blue-400 font-medium">{pendingTransfer.targetName}</span>...
              </p>
              <p className="text-gray-500 text-xs mb-2">
                Caller is hearing music on hold
              </p>
              
              {/* Transfer elapsed timer */}
              {pendingTransfer.startedAt && (
                <TransferTimer startedAt={pendingTransfer.startedAt} />
              )}
              
              <p className="text-gray-500 text-xs mt-4 mb-6 text-center max-w-[260px]">
                If not answered, the call will automatically return to you
              </p>
              
              {/* Cancel Transfer button */}
              <button
                onClick={onCancelTransfer}
                className="px-6 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-full text-sm font-medium transition-colors flex items-center gap-2 active:scale-95"
              >
                <X className="w-4 h-4" />
                Cancel Transfer
              </button>
            </div>
          )}

          {/* Consultation Phase Overlay (target answered, User A can consult before completing) */}
          {attendedTransferState?.status === 'consulting' && callState === 'connected' && (
            <div
              data-testid="sip-consultation-transfer-overlay"
              className="absolute inset-0 bg-gray-950 flex flex-col items-center justify-center z-30 px-6"
            >
              {/* Connected icon */}
              <div className="relative mb-6">
                <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Users className="w-10 h-10 text-green-400" />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <Phone className="w-3 h-3 text-white" />
                </div>
              </div>
              
              <h3 className="text-white text-lg font-semibold mb-1">Consultation connected</h3>
              <p className="text-gray-300 text-sm mb-1">
                Speaking with{' '}
                <span className="font-medium text-green-400">{attendedTransferState.targetName}</span>
              </p>
              <p className="mb-6 max-w-[300px] text-center text-xs text-gray-500">
                The caller is still on hold. Choose whether everyone joins one bridge, or you hand off and drop off.
              </p>

              {/* Action buttons — merge is primary; complete transfer ends your leg (secondary styling). */}
              <div className="flex w-full max-w-[280px] flex-col gap-4">
                {onMergeConference && (
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      aria-label="Merge conference — caller and consultation join the same bridge; you stay connected"
                      aria-busy={attendedTransferSubmitting === 'merge'}
                      disabled={!!attendedTransferSubmitting}
                      onClick={() => {
                        onHapticFeedback('medium');
                        onMergeConference();
                      }}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-blue-400/50 bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/25 transition-colors hover:bg-blue-600 disabled:pointer-events-none disabled:opacity-50 active:scale-95"
                    >
                      {attendedTransferSubmitting === 'merge' ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                      ) : (
                        <GitMerge className="h-4 w-4 shrink-0" aria-hidden />
                      )}
                      Merge conference
                    </button>
                    <p className="text-center text-[10px] leading-snug text-gray-500">
                      Caller comes off hold onto{' '}
                      <span className="text-gray-400">the same bridge</span>; you remain on the call.
                    </p>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    aria-label={`Complete transfer — leave the call; caller stays with ${attendedTransferState.targetName}`}
                    aria-busy={attendedTransferSubmitting === 'complete'}
                    disabled={!!attendedTransferSubmitting}
                    onClick={() => onCompleteTransfer?.()}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/15 disabled:pointer-events-none disabled:opacity-50 active:scale-95"
                  >
                    {attendedTransferSubmitting === 'complete' ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                    ) : (
                      <PhoneForwarded className="h-4 w-4 shrink-0 text-gray-300" aria-hidden />
                    )}
                    Complete transfer
                  </button>
                  <p className="text-center text-[10px] leading-snug text-gray-500">
                    You disconnect — caller stays with{' '}
                    <span className="text-gray-400">{attendedTransferState.targetName}</span>.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Cancel attended transfer and return to caller only"
                  disabled={!!attendedTransferSubmitting}
                  onClick={onCancelAttendedTransfer}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/35 bg-red-500/15 px-6 py-3 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/25 disabled:pointer-events-none disabled:opacity-50 active:scale-95"
                >
                  <X className="h-4 w-4 shrink-0" aria-hidden />
                  Cancel — back to caller
                </button>
              </div>
              <p className="mt-4 max-w-[300px] text-center text-[10px] leading-relaxed text-gray-600">
                Not sure? Stay on consultation — you can retry merge or complete if something fails.
              </p>
            </div>
          )}

          {/* Call Controls - 3-column grid with labels */}
          {(callState === 'connected' || callState === 'initiating' || callState === 'connecting') && (
            <div className="bg-black/60 backdrop-blur-sm px-4 pt-5 pb-[calc(1.25rem+max(env(safe-area-inset-bottom),var(--safe-area-bottom,0px)))]">
              {/* Action Grid */}
              <div className="grid grid-cols-3 gap-y-5 gap-x-4 max-w-[320px] mx-auto mb-6">
                {/* Mute */}
                <button
                  onClick={onToggleMute}
                  className="flex flex-col items-center gap-1.5"
                >
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                    isMuted ? 'bg-white text-black' : 'bg-white/10 text-white'
                  }`}>
                    {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                  </div>
                  <span className={`text-[11px] font-medium ${isMuted ? 'text-white' : 'text-white/60'}`}>
                    {isMuted ? 'Unmute' : 'Mute'}
                  </span>
                </button>
                
                {/* Speaker */}
                {/* Speaker */}
                {canToggleSpeaker && (
                  <button
                    onClick={onToggleSpeaker}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isSpeakerOn ? 'bg-white text-black' : 'bg-white/10 text-white'
                    }`}>
                      <Volume2 className="w-6 h-6" />
                    </div>
                    <span className={`text-[11px] font-medium ${isSpeakerOn ? 'text-white' : 'text-white/60'}`}>
                      Speaker
                    </span>
                  </button>
                )}

                {/* Hold - SIP only / Video toggle for non-SIP */}
                {isSipCall && callState === 'connected' ? (
                  <button
                    onClick={onToggleHold}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isOnHold ? 'bg-yellow-500 text-black' : 'bg-white/10 text-white'
                    }`}>
                      {isOnHold ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
                    </div>
                    <span className={`text-[11px] font-medium ${isOnHold ? 'text-yellow-400' : 'text-white/60'}`}>
                      {isOnHold ? 'Resume' : 'Hold'}
                    </span>
                  </button>
                ) : !isSipCall ? (
                  <button
                    onClick={onToggleVideo}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isVideoEnabled ? 'bg-blue-500 text-white' : 'bg-white/10 text-white'
                    }`}>
                      {isVideoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
                    </div>
                    <span className={`text-[11px] font-medium ${isVideoEnabled ? 'text-blue-400' : 'text-white/60'}`}>
                      Video
                    </span>
                  </button>
                ) : null}

                {/* SIP + RTC: video on the same control surface as hold (mobile dock) */}
                {isSipCall && callState === 'connected' && (
                  <button
                    onClick={onToggleVideo}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isVideoEnabled ? 'bg-blue-500 text-white' : 'bg-white/10 text-white'
                    }`}>
                      {isVideoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
                    </div>
                    <span className={`text-[11px] font-medium ${isVideoEnabled ? 'text-blue-400' : 'text-white/60'}`}>
                      Video
                    </span>
                  </button>
                )}
                
                {/* Transfer - SIP only */}
                {isSipCall && callState === 'connected' && (
                  <button
                    onClick={() => setShowTransferDialog(true)}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center transition-colors hover:bg-white/20">
                      <PhoneForwarded className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-medium text-white/60">Transfer</span>
                  </button>
                )}
                
                {/* Park - SIP only */}
                {isSipCall && callState === 'connected' && (
                  <button
                    onClick={() => setShowParkingDialog(true)}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center transition-colors hover:bg-white/20">
                      <ParkingCircle className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-medium text-white/60">Park</span>
                  </button>
                )}
                
                {/* DTMF Keypad - SIP only */}
                {isSipCall && callState === 'connected' && (
                  <button
                    onClick={() => { setShowDtmfKeypad(true); setDtmfBuffer(''); }}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      showDtmfKeypad ? 'bg-blue-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'
                    }`}>
                      <Grid3X3 className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-medium text-white/60">Keypad</span>
                  </button>
                )}
                
                {/* Switch Camera - video enabled only */}
                {isVideoEnabled && (
                  <button
                    onClick={onSwitchCamera}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center">
                      <SwitchCamera className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-medium text-white/60">Flip</span>
                  </button>
                )}
                
                {/* Background Blur - web/desktop only */}
                {isVideoEnabled && !isCapacitor() && (
                  <button
                    onClick={onToggleBlur}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isBlurEnabled ? 'bg-blue-500 text-white' : 'bg-white/10 text-white'
                    }`}>
                      <Sparkles className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-medium text-white/60">Blur</span>
                  </button>
                )}
                
                {/* Screen Share - web only, not for SIP */}
                {!isCapacitor() && !isSipCall && (
                  <button
                    onClick={onToggleScreenShare}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isScreenSharing ? 'bg-blue-500 text-white' : 'bg-white/10 text-white'
                    }`}>
                      <MonitorUp className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-medium text-white/60">Share</span>
                  </button>
                )}
                
                {/* Pop out - web/desktop only */}
                {!isCapacitor() && callState === 'connected' && (
                  <button
                    onClick={onPopOut}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className="relative w-14 h-14 rounded-full bg-indigo-600 text-white flex items-center justify-center transition-colors hover:bg-indigo-500">
                      <PictureInPicture2 className="w-6 h-6" />
                      <span className="absolute top-0 right-0 flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500"></span>
                      </span>
                    </div>
                    <span className="text-[11px] font-medium text-white/60">Pop Out</span>
                  </button>
                )}
                
                {/* Device Switcher */}
                {callState === 'connected' && activeSessions.filter(s => !s.is_current).length > 0 && (
                  <button
                    onClick={() => setShowDeviceSwitcher(true)}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center">
                      <ArrowRightLeft className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-medium text-white/60">Switch</span>
                  </button>
                )}
                
                {/* Add Participants - not for SIP */}
                {callState === 'connected' && !isSipCall && (
                  <button
                    onClick={() => setShowAddParticipants(true)}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center">
                      <UserPlus className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-medium text-white/60">Add Call</span>
                  </button>
                )}
                
                {/* Hand Raise - not for SIP */}
                {callState === 'connected' && !isSipCall && (
                  <button
                    onClick={onToggleHandRaise}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isHandRaised ? 'bg-yellow-500 text-black' : 'bg-white/10 text-white'
                    }`}>
                      <Hand className={`w-6 h-6 ${isHandRaised ? 'animate-bounce' : ''}`} />
                    </div>
                    <span className={`text-[11px] font-medium ${isHandRaised ? 'text-yellow-400' : 'text-white/60'}`}>
                      {isHandRaised ? 'Lower' : 'Raise'}
                    </span>
                  </button>
                )}
                
                {/* Reactions - not for SIP */}
                {callState === 'connected' && !isSipCall && (
                  <div className="relative flex flex-col items-center gap-1.5">
                    <button
                      onClick={() => setShowReactionPicker(!showReactionPicker)}
                      className="flex flex-col items-center gap-1.5"
                    >
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                        showReactionPicker ? 'bg-purple-500 text-white' : 'bg-white/10 text-white'
                      }`}>
                        <Smile className="w-6 h-6" />
                      </div>
                      <span className="text-[11px] font-medium text-white/60">React</span>
                    </button>
                    
                    {showReactionPicker && (
                      <div className="absolute bottom-[72px] left-1/2 -translate-x-1/2 bg-gray-800 rounded-2xl p-2 shadow-xl flex gap-1 animate-in fade-in slide-in-from-bottom-3 z-10">
                        {CALL_REACTIONS.map(({ emoji, label }) => (
                          <button
                            key={emoji}
                            onClick={() => handleSendReaction(emoji)}
                            className="w-9 h-9 flex items-center justify-center text-xl hover:bg-gray-700 rounded-lg transition-colors hover:scale-125"
                            title={label}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Whiteboard - Desktop/Web only, not for SIP */}
                {callState === 'connected' && !isCapacitor() && !isSipCall && meetingSettingsConfig?.allowWhiteboard && (
                  <button
                    type="button"
                    onClick={() => onToggleWhiteboard(!showWhiteboard)}
                    className="flex flex-col items-center gap-1.5 max-w-[4.25rem]"
                    title={
                      remotePresenterBoardActive && !showWhiteboard && remotePresenterBoardName
                        ? `${remotePresenterBoardName} is presenting the whiteboard — open to watch`
                        : 'Whiteboard'
                    }
                  >
                    <div
                      className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                        showWhiteboard
                          ? 'bg-green-500 text-white'
                          : remotePresenterBoardActive
                            ? 'bg-emerald-500/90 text-white ring-2 ring-emerald-300/50'
                            : 'bg-white/10 text-white'
                      }`}
                    >
                      <PenLine className="w-6 h-6" />
                      {remotePresenterBoardActive && !showWhiteboard && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-[10px] font-medium leading-tight text-center line-clamp-2 ${
                        showWhiteboard ? 'text-emerald-400' : remotePresenterBoardActive ? 'text-emerald-300' : 'text-white/60'
                      }`}
                    >
                      {showWhiteboard
                        ? 'Board'
                        : remotePresenterBoardActive && remotePresenterBoardName
                          ? `${remotePresenterBoardName.split(' ')[0] || 'Peer'} · Live`
                          : 'Board'}
                    </span>
                  </button>
                )}
                
                {/* Recording - Desktop/Web only, not for SIP */}
                {callState === 'connected' && !isCapacitor() && !isSipCall && (
                  <button
                    onClick={onToggleRecording}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isRecording ? 'bg-red-600 text-white' : 'bg-white/10 text-white'
                    }`}>
                      <Circle className={`w-6 h-6 ${isRecording ? 'fill-current animate-pulse' : ''}`} />
                      {isRecording && (
                        <span className="absolute top-0 right-0 flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                        </span>
                      )}
                    </div>
                    <span className={`text-[11px] font-medium ${isRecording ? 'text-red-400' : 'text-white/60'}`}>
                      {isRecording ? 'Recording' : 'Record'}
                    </span>
                  </button>
                )}
                
                {/* Lobby indicator */}
                {callState === 'connected' && isGroupCall && waitingParticipants.length > 0 && (
                  <button
                    onClick={() => onToggleParticipantsPanel(true)}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className="relative w-14 h-14 rounded-full bg-amber-500 text-white flex items-center justify-center">
                      <DoorOpen className="w-6 h-6" />
                      <span className="absolute -top-1 -right-1 bg-white text-amber-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {waitingParticipants.length}
                      </span>
                    </div>
                    <span className="text-[11px] font-medium text-amber-400">Lobby</span>
                  </button>
                )}
                
                {/* Breakout Rooms - group calls only */}
                {callState === 'connected' && isGroupCall && !isCapacitor() && (
                  <button
                    onClick={onShowBreakoutModal}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <div className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isBreakoutActive ? 'bg-purple-500 text-white' : 'bg-white/10 text-white'
                    }`}>
                      <Grid3X3 className="w-6 h-6" />
                      {isBreakoutActive && (
                        <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                          {breakoutRooms.length}
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] font-medium text-white/60">Rooms</span>
                  </button>
                )}
              </div>
              
              {/* End Call Button - separated at bottom (rail has End on xl+) */}
              <div className="flex flex-col items-center lg:hidden">
                <button
                  onClick={handleEndCall}
                  className="w-16 h-16 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg shadow-red-500/30 transition-all duration-200 active:scale-90"
                >
                  <PhoneOff className="w-7 h-7" />
                </button>
                <span className="text-red-400/80 text-[11px] font-medium mt-1.5">End</span>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
      
      {/* Safe area bottom spacer - use max() for Android Capacitor CSS variable fallback */}
      <div className="h-[max(env(safe-area-inset-bottom),var(--safe-area-bottom,0px))] flex-shrink-0 bg-black" />
      
      {/* Device Switcher Modal */}
      {showDeviceSwitcher && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl w-full max-w-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Switch Device</h3>
              <button
                onClick={() => setShowDeviceSwitcher(false)}
                className="p-2 hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-2 max-h-[300px] overflow-y-auto">
              {loadingSessions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : activeSessions.filter(s => !s.is_current).length === 0 ? (
                <p className="text-gray-400 text-center py-8">No other active sessions</p>
              ) : (
                activeSessions.filter(s => !s.is_current).map((session) => {
                  const PlatformIcon = session.platform === 'desktop' ? Monitor : 
                                      session.platform === 'mobile' ? Smartphone : Globe;
                  const isTransferring = transferringTo === session.session_id;
                  const platformLabel = session.platform === 'desktop' ? 'Desktop' : 
                                       session.platform === 'mobile' ? 'Mobile' : 'Web';
                  
                  return (
                    <button
                      key={session.session_id}
                      onClick={() => onTransferToDevice(session.session_id)}
                      disabled={isTransferring}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                        isTransferring ? 'bg-blue-600/30' : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-600">
                        {isTransferring ? (
                          <Loader2 className="w-5 h-5 text-white animate-spin" />
                        ) : (
                          <PlatformIcon className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-white font-medium">{session.device_name}</p>
                        <p className="text-gray-400 text-sm">{platformLabel}</p>
                      </div>
                      {!isTransferring && (
                        <ArrowRightLeft className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-700">
              <p className="text-xs text-gray-500 text-center">
                Transfer this call to another device while staying connected
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Transfer Dialog - contacts-style with avatars */}
      {showTransferDialog && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full max-w-sm overflow-hidden animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 fade-in duration-200">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <PhoneForwarded className="w-5 h-5 text-blue-400" />
                Transfer Call
              </h3>
              <button
                onClick={() => { setShowTransferDialog(false); setTransferSearch(''); }}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            {/* Search */}
            <div className="p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or extension..."
                  value={transferSearch}
                  onChange={(e) => setTransferSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  autoFocus
                />
              </div>
            </div>
            
            {/* User/Extension List */}
            <div className="px-3 pb-3 space-y-1.5 max-h-[300px] overflow-y-auto">
              {loadingExtensions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : filteredTransferTargets.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 mx-auto mb-2 text-gray-600" />
                  <p className="text-gray-400 text-sm">
                    {extensionList.length === 0 ? 'No extensions available' : 'No matching contacts'}
                  </p>
                </div>
              ) : (
                filteredTransferTargets.map((target) => (
                  <button
                    key={target.id}
                    onClick={() => {
                      if (target.is_dnd) return; // Blocked
                      onTransfer(target.extension_number, target.display_name);
                      setShowTransferDialog(false);
                      setTransferSearch('');
                    }}
                    disabled={target.is_dnd}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                      target.is_dnd
                        ? 'bg-gray-800/50 opacity-50 cursor-not-allowed'
                        : 'bg-gray-700 hover:bg-gray-600 active:scale-[0.98]'
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${
                        target.is_dnd ? 'bg-gray-700 ring-2 ring-red-500/50' : 'bg-gray-600'
                      }`}>
                        {target.avatar_url ? (
                          <img src={target.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className={`w-5 h-5 ${target.is_dnd ? 'text-gray-500' : 'text-gray-300'}`} />
                        )}
                      </div>
                      {/* Online/Offline indicator */}
                      <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-gray-800 ${
                        target.is_dnd ? 'bg-red-500' : target.is_online ? 'bg-green-500' : 'bg-gray-500'
                      }`} />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium text-sm truncate ${
                          target.is_dnd ? 'text-gray-500' : 'text-white'
                        }`}>
                          {target.display_name}
                        </p>
                        {target.is_dnd && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/20 text-red-400 text-[10px] rounded-full flex-shrink-0">
                            <BellOff className="w-2.5 h-2.5" />
                            DND
                          </span>
                        )}
                      </div>
                      <p className={`text-xs truncate ${target.is_dnd ? 'text-gray-600' : 'text-gray-400'}`}>
                        Ext. {target.friendly_extension || target.extension_number}
                        {target.rolling_enabled && (
                          <span className="ml-1" title="Rolling extension">🔒</span>
                        )}
                      </p>
                    </div>
                    
                    {/* Transfer icon */}
                    {!target.is_dnd && (
                      <PhoneForwarded className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
            
            <div className="px-4 py-2.5 border-t border-gray-700 pb-[calc(0.625rem+max(env(safe-area-inset-bottom),var(--safe-area-bottom,0px)))]">
              <p className="text-xs text-gray-500 text-center">
                Caller will hear music on hold while ringing the target
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Parking Dialog */}
      {showParkingDialog && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl w-full max-w-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Park Call</h3>
              <button
                onClick={() => setShowParkingDialog(false)}
                className="p-2 hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-gray-400 text-sm mb-4">
                Select a parking slot to park this call.
              </p>
              
              <div className="grid grid-cols-4 gap-2">
                {loadingParkingSlots ? (
                  <div className="col-span-4 flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                  </div>
                ) : parkingSlots.length === 0 ? (
                  Array.from({ length: 20 }, (_, i) => 701 + i).map(slot => (
                    <button
                      key={slot}
                      onClick={() => {
                        onParkCall(slot);
                        setShowParkingDialog(false);
                      }}
                      className="p-3 rounded-lg text-center transition-colors bg-gray-700 hover:bg-gray-600 text-white"
                    >
                      <span className="text-sm font-medium">{slot}</span>
                    </button>
                  ))
                ) : (
                  parkingSlots.map(slot => (
                    <button
                      key={slot.slot}
                      onClick={() => {
                        onParkCall(slot.slot);
                        setShowParkingDialog(false);
                      }}
                      className={`p-3 rounded-lg text-center transition-colors ${
                        slot.is_occupied
                          ? 'bg-green-700 hover:bg-green-600 text-white'
                          : 'bg-gray-700 hover:bg-gray-600 text-white'
                      }`}
                      title={slot.is_occupied ? `${slot.caller_name || slot.caller_number} - Click to retrieve` : 'Available'}
                    >
                      <span className="text-sm font-medium block">{slot.slot}</span>
                      {slot.is_occupied && (
                        <Phone className="w-3 h-3 mx-auto mt-1 text-green-300" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
            
            <div className="px-4 py-3 border-t border-gray-700">
              <p className="text-xs text-gray-500 text-center">
                Green slots have parked calls. Click to retrieve.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* DTMF Keypad Modal */}
      {showDtmfKeypad && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full max-w-xs overflow-hidden animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 fade-in duration-200">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Grid3X3 className="w-5 h-5 text-blue-400" />
                Keypad
              </h3>
              <button
                onClick={() => setShowDtmfKeypad(false)}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            {/* DTMF Buffer Display */}
            <div className="px-6 py-3">
              <div className={`text-center text-2xl font-mono tracking-wider min-h-[36px] ${
                dtmfBuffer ? 'text-white' : 'text-gray-600'
              }`}>
                {dtmfBuffer || 'Press a key'}
              </div>
            </div>
            
            {/* Keypad Grid */}
            <div className="px-6 pb-4">
              <div className="grid grid-cols-3 gap-2">
                {dtmfDigits.map(({ num, letters }) => (
                  <button
                    key={num}
                    onClick={() => handleDtmfDigit(num)}
                    className="w-full aspect-square max-h-[64px] rounded-full flex flex-col items-center justify-center transition-all duration-100 active:scale-90 select-none bg-gray-700 hover:bg-gray-600 active:bg-blue-600 ring-1 ring-gray-600/50"
                  >
                    <span className="text-xl font-semibold text-white">{num}</span>
                    {letters && (
                      <span className="text-[8px] tracking-widest font-medium text-gray-400">{letters}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-700 pb-[calc(0.75rem+max(env(safe-area-inset-bottom),var(--safe-area-bottom,0px)))]">
              <p className="text-xs text-gray-500 text-center">
                Send DTMF tones during active call
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Participants Modal */}
      {showAddParticipants && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl w-full max-w-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Add to Call</h3>
              <button
                onClick={() => setShowAddParticipants(false)}
                className="p-2 hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            {/* Generate Invite Link */}
            <div className="p-4 border-b border-gray-700">
              <button
                onClick={() => {
                  if (onGenerateInviteLink) {
                    onGenerateInviteLink();
                    setShowAddParticipants(false);
                  }
                }}
                disabled={generatingLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-xl transition-colors"
              >
                {generatingLink ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Link2 className="w-5 h-5" />
                )}
                <span>Generate Invite Link</span>
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Share a link for external guests to join
              </p>
            </div>
            
            {/* Search Users */}
            <div className="px-4 py-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search team members..."
                  value={addParticipantSearch}
                  onChange={(e) => setAddParticipantSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-gray-700 text-white border border-gray-600 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            
            {/* Users List */}
            <div className="p-4 pt-0 space-y-2 max-h-[250px] overflow-y-auto">
              {(operationsUsers || [])
                .filter(u => {
                  // Exclude users already in the call
                  const participantUserIds = participants.map(p => p.userId);
                  if (participantUserIds.includes(u.id)) return false;
                  // Apply search filter
                  if (!addParticipantSearch) return true;
                  const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
                  return fullName.includes(addParticipantSearch.toLowerCase());
                })
                .slice(0, 10)
                .map((opUser) => {
                  const isAdding = addingParticipant === opUser.id;
                  return (
                    <button
                      key={opUser.id}
                      onClick={() => {
                        onAddParticipant(opUser);
                        setShowAddParticipants(false);
                        setAddParticipantSearch('');
                      }}
                      disabled={isAdding}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                        isAdding ? 'bg-green-600/30' : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden">
                        {opUser.avatar_url ? (
                          <img src={opUser.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : isAdding ? (
                          <Loader2 className="w-5 h-5 text-white animate-spin" />
                        ) : (
                          <User className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-white font-medium">
                          {opUser.first_name} {opUser.last_name}
                        </p>
                        <p className="text-gray-400 text-sm">
                          {opUser.module_role || 'Team Member'}
                        </p>
                      </div>
                      {!isAdding && (
                        <Phone className="w-5 h-5 text-green-400" />
                      )}
                    </button>
                  );
                })
              }
              {(operationsUsers || []).length === 0 && (
                <p className="text-gray-400 text-center py-4">No team members found</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Invite Link Modal */}
      {showInviteLink && inviteLink && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl w-full max-w-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Invite Link</h3>
              <button
                onClick={onCloseInviteLink}
                className="p-2 hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-400">
                Share this link to invite someone to join the call:
              </p>
              
              <div className="bg-gray-700 rounded-lg p-3 flex items-center gap-2">
                <input
                  type="text"
                  value={inviteLink}
                  readOnly
                  className="flex-1 bg-transparent text-white text-sm truncate"
                />
                <button
                  onClick={onCopyInviteLink}
                  className="p-2 hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                  title="Copy link"
                >
                  <Copy className="w-5 h-5 text-gray-300" />
                </button>
              </div>
              
              <button
                onClick={onCopyInviteLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors"
              >
                <Copy className="w-5 h-5" />
                <span>Copy Link</span>
              </button>
              
              <p className="text-xs text-gray-500 text-center">
                Link expires in 1 hour · Max 5 uses
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InCallUI;
